import { adminRoute } from '../config/paths';
import { adminClient } from './client';
import { messageFromOrchestratorAxiosError } from '../lib/axiosErrorMessage';
import { SUPPRESS_ADMIN_GLOBAL_ERROR } from '../lib/adminApiErrorPolicy';

export const ORCHESTRATOR_AUTH_ME_QUERY_KEY = ['orchestratorAuthMe'] as const;

/** Mirrors orchestrator `AuthenticatedSessionDto` (subset used by the SPA). */
export type OrchestratorSessionDto = {
  authenticated: boolean;
  userId?: string;
  email?: string;
  displayName?: string;
  emailVerified?: boolean;
  mfaEnabled?: boolean;
  mfaVerified?: boolean;
  /** Password OK; TOTP/recovery step-up still required (do not logout/wipe). */
  mfaChallengePending?: boolean;
  authStrength?: 'pwd' | 'sso' | 'mfa';
  expiresAt?: string;
  message?: string;
};

export type MfaSetupResponseDto = {
  secret: string;
  qrCode: string;
  otpAuthUrl: string;
};

export type MfaResponseDto = {
  success: boolean;
  message: string;
  redirectTo?: string | null;
  remainingRecoveryCodes?: number;
};

export type RecoveryCodesResponseDto = {
  success: boolean;
  message: string;
  recoveryCodes: string[];
};

/** Loose mirror of orchestrator `AuthResponseDto` for login/signup responses. */
export type OrchestratorAuthResponseDto = {
  token?: string;
  userId?: string;
  email?: string;
  displayName?: string;
  mfaRequired?: boolean;
  mfaEnabled?: boolean;
  mfaVerified?: boolean;
  requiresEmailVerification?: boolean;
  verificationRequired?: boolean;
  resumed?: boolean;
  throttled?: boolean;
  cooldownSeconds?: number;
  cooldownUntil?: string;
  message?: string;
};

export type EmailVerificationResponseDto = {
  success: boolean;
  email?: string;
  message: string;
  cooldownSeconds?: number;
  cooldownUntil?: string;
};

/**
 * Browser → orchestrator auth (same paths as Cash4You: `/auth/*` under `/api/orchestrator/v1`).
 * Uses cookie session (`withCredentials` on `adminClient`), not admin-api JWT.
 */
export const orchestratorAuthApi = {
  async login(email: string, password: string): Promise<OrchestratorAuthResponseDto> {
    const r = await adminClient.post<OrchestratorAuthResponseDto>('/auth/login', {
      email,
      password,
      sessionProfile: 'operator',
    });
    return r.data;
  },

  async signup(body: { name?: string; email: string; password: string }): Promise<OrchestratorAuthResponseDto> {
    const r = await adminClient.post<OrchestratorAuthResponseDto>('/auth/signup', {
      ...body,
      sessionProfile: 'operator',
    });
    return r.data;
  },

  async sendEmailVerification(email: string): Promise<EmailVerificationResponseDto> {
    const r = await adminClient.post<EmailVerificationResponseDto>('/auth/email/send-verification', { email });
    return r.data;
  },

  async verifyEmail(email: string, code: string): Promise<EmailVerificationResponseDto> {
    const r = await adminClient.post<EmailVerificationResponseDto>('/auth/email/verify', { email, code });
    return r.data;
  },

  async requestPasswordReset(email: string): Promise<{
    success: boolean;
    message: string;
    cooldownSeconds?: number;
    cooldownUntil?: string;
  }> {
    const r = await adminClient.post('/auth/password-reset/request', { email });
    return r.data;
  },

  async verifyPasswordResetOtp(body: { email: string; code: string }): Promise<{
    success: boolean;
    message: string;
    email?: string;
    token?: string;
  }> {
    const r = await adminClient.post('/auth/password-reset/verify', body);
    return r.data;
  },

  async confirmPasswordReset(body: {
    password: string;
    confirmPassword: string;
    token: string;
  }): Promise<{ success: boolean; message: string }> {
    const r = await adminClient.post('/auth/password-reset/confirm', body);
    return r.data;
  },

  async logout(): Promise<void> {
    await adminClient.post('/auth/logout', {});
  },

  async me(): Promise<OrchestratorSessionDto> {
    try {
      const r = await adminClient.get<OrchestratorSessionDto>('/auth/me', {
        // Expected when signed out — do not treat as a hard transport failure.
        validateStatus: (status) => (status >= 200 && status < 300) || status === 401,
      });
      if (r.status === 401) {
        const body = r.data;
        if (body && typeof body === 'object' && !Array.isArray(body)) {
          const o = body as Record<string, unknown>;
          if (o.authenticated === false) {
            return {
              authenticated: false,
              userId: typeof o.userId === 'string' ? o.userId : undefined,
              email: typeof o.email === 'string' ? o.email : undefined,
              message: typeof o.message === 'string' ? o.message : undefined,
              mfaChallengePending: o.mfaChallengePending === true,
              mfaEnabled: o.mfaEnabled === true,
              mfaVerified: o.mfaVerified === true,
            };
          }
          // Global error envelope (`status: FAILED`) — still means no session.
          if (o.status === 'FAILED' || o.authenticated === undefined) {
            return { authenticated: false };
          }
        }
        return { authenticated: false };
      }
      return r.data;
    } catch (err) {
      // Network / CORS / orchestrator down — rethrow for probe.reason === 'network'
      throw err;
    }
  },

  async setupMfa(): Promise<MfaSetupResponseDto> {
    const r = await adminClient.post<MfaSetupResponseDto>('/auth/mfa/setup', {});
    return r.data;
  },

  async enableMfa(code: string): Promise<RecoveryCodesResponseDto> {
    const r = await adminClient.post<RecoveryCodesResponseDto>('/auth/mfa/enable', { code });
    return r.data;
  },

  async verifyMfa(code: string, rememberDevice = false): Promise<MfaResponseDto> {
    const r = await adminClient.post<MfaResponseDto>(
      '/auth/mfa/verify',
      { code, rememberDevice },
      {
        ...SUPPRESS_ADMIN_GLOBAL_ERROR,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 401,
      },
    );
    if (r.status === 401) {
      const body = r.data;
      if (body && typeof body === 'object') {
        return {
          success: body.success === true,
          message:
            typeof body.message === 'string' && body.message.trim()
              ? body.message.trim()
              : 'That code is invalid or has expired. Enter the current 6-digit code from your authenticator app.',
          redirectTo: body.redirectTo ?? null,
          remainingRecoveryCodes: body.remainingRecoveryCodes,
        };
      }
      return {
        success: false,
        message: 'That code is invalid or has expired. Enter the current 6-digit code from your authenticator app.',
      };
    }
    return r.data;
  },

  async verifyMfaRecoveryCode(code: string): Promise<MfaResponseDto> {
    const r = await adminClient.post<MfaResponseDto>(
      '/auth/mfa/recovery',
      { code },
      {
        ...SUPPRESS_ADMIN_GLOBAL_ERROR,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 401,
      },
    );
    if (r.status === 401) {
      const body = r.data;
      if (body && typeof body === 'object') {
        return {
          success: body.success === true,
          message:
            typeof body.message === 'string' && body.message.trim()
              ? body.message.trim()
              : 'That recovery code is invalid or already used.',
          redirectTo: body.redirectTo ?? null,
          remainingRecoveryCodes: body.remainingRecoveryCodes,
        };
      }
      return {
        success: false,
        message: 'That recovery code is invalid or already used.',
      };
    }
    return r.data;
  },

  async commitMfaLogin(): Promise<MfaResponseDto> {
    const r = await adminClient.post<MfaResponseDto>(
      '/auth/mfa/commit',
      {},
      {
        ...SUPPRESS_ADMIN_GLOBAL_ERROR,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 409,
      },
    );
    if (r.status === 409) {
      const body = r.data;
      if (body && typeof body === 'object') {
        return {
          success: body.success === true,
          message:
            typeof body.message === 'string' && body.message.trim()
              ? body.message.trim()
              : 'MFA verification is required before completing sign-in.',
          redirectTo: body.redirectTo ?? null,
          remainingRecoveryCodes: body.remainingRecoveryCodes,
        };
      }
      return {
        success: false,
        message: 'MFA verification is required before completing sign-in.',
      };
    }
    return r.data;
  },

  /**
   * RBAC Phase 2: mints a 60-second MFA attestation for the caller's own operator session, for
   * admin-api `/auth/login` and `/auth/mfa/step-up`. 401 `MFA_REQUIRED` when the session is not
   * MFA (e.g. SSO), 503 when the shared secret is not configured. The caller handles failures.
   */
  async consoleMfaAttestation(): Promise<{ attestation: string; expiresAt: string }> {
    const r = await adminClient.post<{ attestation: string; expiresAt: string }>(
      '/admin/console-mfa-attestation',
      undefined,
      SUPPRESS_ADMIN_GLOBAL_ERROR,
    );
    if (!r.data || typeof r.data.attestation !== 'string' || !r.data.attestation) {
      throw new Error('The orchestrator returned no MFA attestation.');
    }
    return r.data;
  },

  async disableMfa(input: { code?: string; recoveryCode?: string }): Promise<MfaResponseDto> {
    const r = await adminClient.post<MfaResponseDto>('/auth/mfa/disable', input);
    return r.data;
  },

  async regenerateMfaRecoveryCodes(): Promise<RecoveryCodesResponseDto> {
    const r = await adminClient.post<RecoveryCodesResponseDto>(
      '/auth/mfa/recovery-codes/regenerate',
      {},
    );
    return r.data;
  },
};

/** Absolute orchestrator JSON root for top-level navigations (OAuth), matching `adminClient` resolution. */
export function orchestratorV1BaseUrl(): string {
  const raw = import.meta.env.VITE_ORCHESTRATOR_API_BASE;
  const base = typeof raw === 'string' ? raw.trim().replace(/\/$/, '') : '';
  if (!base) {
    if (import.meta.env.DEV) {
      return 'http://localhost:3000/api/orchestrator/v1';
    }
    return `${window.location.origin}/api/orchestrator/v1`;
  }
  return `${base}/api/orchestrator/v1`;
}

/**
 * OAuth must tag `app=operator` so the orchestrator uses the operator session cookie
 * (`orch.sid.op`), separate from the customer app (`orch.sid`).
 * `returnTo` is sent as an absolute admin SPA URL so post-login redirect does not use
 * FRONTEND_APP_BASE_URL (customer).
 */
function isUnauthenticatedOrchestratorError(err: unknown): boolean {
  const ax = err as { response?: { status?: number } };
  if (ax.response?.status === 401) return true;
  const m = messageFromOrchestratorAxiosError(err);
  return /not authenticated|401|no active session/i.test(m);
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Best-effort logout + poll until `/auth/me` stops returning an authenticated session (Cash4You parity). */
export async function orchestratorLogoutEverywhere(): Promise<void> {
  try {
    await orchestratorAuthApi.logout();
  } catch (err) {
    if (!isUnauthenticatedOrchestratorError(err)) throw err;
  }

  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      const session = await orchestratorAuthApi.me();
      if (!session.authenticated) return;
    } catch (err) {
      if (isUnauthenticatedOrchestratorError(err)) return;
      throw err;
    }

    if (attempt < 3) {
      await wait(250);
    }
  }

  throw new Error('Logout did not clear the active session.');
}

export function startOrchestratorOAuth(provider: 'google' | 'bizauth', returnPathOrUrl: string): void {
  const root = orchestratorV1BaseUrl();
  const url = new URL(`${root.replace(/\/$/, '')}/auth/oauth/start`);
  url.searchParams.set('provider', provider);
  url.searchParams.set('app', 'operator');

  const path = returnPathOrUrl.startsWith('/') ? returnPathOrUrl : `/${returnPathOrUrl}`;
  const absolute = /^https?:\/\//i.test(returnPathOrUrl)
    ? returnPathOrUrl
    : `${window.location.origin}${adminRoute(path)}`;
  url.searchParams.set('returnTo', absolute);

  window.location.assign(url.toString());
}
